import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import {
  getReclaimCommercialTerms,
  getReclaimCommercialTermsCopy,
} from "@redotech/redo-dispute-common/reclaim-onboarding-v2-mirror";
import type {
  ReclaimAccessDefaults,
  ReclaimAccessSource,
} from "@redotech/redo-dispute-settings-common/reclaim-access-settings";
import { DEFAULT_RECLAIM_ACCESS_DEFAULTS } from "@redotech/redo-dispute-settings-common/reclaim-access-settings";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useContext, useMemo, type ContextType } from "react";

type ReclaimRpcClient = NonNullable<
  ContextType<typeof RedoMerchantRpcClientContext>
>;

let cachedReclaimAccessDefaults: ReclaimAccessDefaults | null = null;
let pendingReclaimAccessDefaults: Promise<ReclaimAccessDefaults> | null = null;

export function useReclaimAccessDefaults({
  enabled = true,
  source,
}: { enabled?: boolean; source?: ReclaimAccessSource } = {}): {
  accessDefaults: ReclaimAccessDefaults;
  error: unknown;
  pending: boolean;
  terms: ReturnType<typeof getReclaimCommercialTerms>;
  termsCopy: ReturnType<typeof getReclaimCommercialTermsCopy>;
} {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const team = useContext(TeamContext);
  const load = useLoad(
    async () =>
      enabled
        ? loadReclaimAccessDefaults({ rpcClient })
        : DEFAULT_RECLAIM_ACCESS_DEFAULTS,
    [enabled, rpcClient],
  );
  // Defaults are not sensitive and only affect merchant copy/access affordances.
  // If the read fails, keep the UI usable with the static product defaults.
  const accessDefaults =
    load.value ??
    cachedReclaimAccessDefaults ??
    DEFAULT_RECLAIM_ACCESS_DEFAULTS;
  const terms = useMemo(
    () =>
      getReclaimCommercialTerms({
        accessDefaults,
        disputes: team?.settings.disputes,
        source,
      }),
    [accessDefaults, source, team?.settings.disputes],
  );
  const termsCopy = useMemo(
    () =>
      getReclaimCommercialTermsCopy({
        accessDefaults,
        disputes: team?.settings.disputes,
        source,
      }),
    [accessDefaults, source, team?.settings.disputes],
  );

  return {
    accessDefaults,
    error: load.error,
    pending:
      enabled && !load.error && !load.value && !cachedReclaimAccessDefaults,
    terms,
    termsCopy,
  };
}

export function clearReclaimAccessDefaultsCacheForTest(): void {
  cachedReclaimAccessDefaults = null;
  pendingReclaimAccessDefaults = null;
}

async function loadReclaimAccessDefaults({
  rpcClient,
}: {
  rpcClient: ReclaimRpcClient;
}): Promise<ReclaimAccessDefaults> {
  if (cachedReclaimAccessDefaults) {
    return cachedReclaimAccessDefaults;
  }
  if (!pendingReclaimAccessDefaults) {
    pendingReclaimAccessDefaults = rpcClient
      .getReclaimAccessDefaults({})
      .then((result) => {
        // The merchant RPC now returns the full ReclaimAccessDefaults
        // including admin-configured sourceRules. Cache and return as-is —
        // we no longer strip + rehydrate with build-time constants, which
        // silently ignored every admin edit to source rules.
        cachedReclaimAccessDefaults = result.defaults;
        return result.defaults;
      })
      .catch(() => DEFAULT_RECLAIM_ACCESS_DEFAULTS)
      .finally(() => {
        pendingReclaimAccessDefaults = null;
      });
  }
  return pendingReclaimAccessDefaults;
}
